import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from 'fronton-react';
import { InfoIcon } from '@fronton/icons-react';
import { InfoBar } from 'common/components';
import { useEffectOnce } from 'common/hooks';
import { checkScreenType } from 'utils';
import { GAShow } from 'utils/analytics';
import { selectPageModelTitle } from '../../../../../store/pageModel';
import { selectWindowType, WindowTypes } from '../../../../../store/pageStore';
import { InstoreService, InstoreServiceGroup } from '../../types';
import {
	categoryNameStyles,
	priceTd,
	serviceDescriptionStyles,
	StyledTable,
	StyledTd,
	StyledTr,
	StyledUl,
	titleStyles,
	Wrapper,
} from './styles';

type ServicesProps = {
	servicesTitle: string;
	serviceGroups: InstoreServiceGroup[];
	selectedStoreCode: number | null;
};
export const Services = memo(
	({ servicesTitle, serviceGroups, selectedStoreCode }: ServicesProps) => {
		const pageTitle = useSelector(selectPageModelTitle);

		useEffectOnce(() => {
			if (!serviceGroups.length && selectedStoreCode) {
				GAShow({
					elementBlock: 'servicePage',
					elementName: pageTitle,
					elementText: 'Услуга не оказывается в выбранном магазине',
				});
			}
		});

		if (!serviceGroups.length && !selectedStoreCode) {
			return null;
		}

		if (!serviceGroups.length && selectedStoreCode) {
			return (
				<InfoBar
					type="alert"
					icon={<InfoIcon type="outline" size="l" color="text-primary" />}
					title="Услуга не оказывается в выбранном магазине"
				/>
			);
		}

		return (
			<>
				<ServicesTitle servicesTitle={servicesTitle} />
				{serviceGroups.map((group, key) => (
					<ServiceGroup key={`group-${key}`} {...group} />
				))}
			</>
		);
	}
);

type ServiceGroupProps = InstoreServiceGroup;

export const ServiceGroup = memo(({ category, services }: ServiceGroupProps) => {
	return (
		<Wrapper data-qa="instore-service-group">
			{category && (
				<Typography
					as="h3"
					variant="m"
					size="body_accent"
					className={categoryNameStyles}
					data-qa="instore-service-group-name"
				>
					{category}
				</Typography>
			)}
			<StyledTable>
				<tbody>
					{services.map(service => (
						<ServiceRow key={`service-${service.name}`} {...service} />
					))}
				</tbody>
			</StyledTable>
		</Wrapper>
	);
});

export const ServiceRow = memo(({ name, descriptions, price, measurementUnit }: InstoreService) => {
	return (
		<StyledTr data-qa="instore-service">
			<StyledTd>
				<Typography variant="m" size="body_long" data-qa="instore-service-name">
					{name}
				</Typography>
				{descriptions.length > 0 && (
					<StyledUl>
						{descriptions.map(item => (
							<li key={`description-${item}`} data-qa="instore-service-description">
								<Typography
									variant="s"
									size="body_long"
									className={serviceDescriptionStyles}
									overrideStyles={false}
								>
									{item}
								</Typography>
							</li>
						))}
					</StyledUl>
				)}
			</StyledTd>
			<StyledTd className={priceTd}>
				<Typography variant="m" size="body_accent" data-qa="instore-service-price">
					{price === null
						? 'Согласно проекту'
						: `${price}\u00A0₽\u00A0/\u00A0${measurementUnit.name}`}
				</Typography>
			</StyledTd>
		</StyledTr>
	);
});

type ServicesTitleProps = {
	servicesTitle: string;
};

export const ServicesTitle = memo(({ servicesTitle }: ServicesTitleProps) => {
	const currentWindowType = useSelector(selectWindowType);
	const isMobile = checkScreenType(currentWindowType, WindowTypes.mobile);
	const titleVariant = useMemo(() => (isMobile ? 'h2' : 'h3'), [isMobile]);

	return (
		<Typography as="h3" variant={titleVariant} className={titleStyles} overrideStyles={false}>
			{servicesTitle}
		</Typography>
	);
});
